import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, FolderTree } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import "../../styles/CatalogMegaMenu.css";

const copyByLocale = {
  nl: { products: "Producten", all: "Alle producten", level1: "Hoofdgroepen", level2: "Subgroepen", level3: "Sub-subgroepen", choose: "Kies een groep", error: "Productmenu kon niet worden geladen" },
  de: { products: "Produkte", all: "Alle Produkte", level1: "Hauptgruppen", level2: "Untergruppen", level3: "Produktgruppen", choose: "Gruppe auswählen", error: "Produktmenü konnte nicht geladen werden" },
  en: { products: "Products", all: "All products", level1: "Main groups", level2: "Subgroups", level3: "Product groups", choose: "Choose a group", error: "Product menu could not be loaded" },
};

const MenuRow = ({ group, level, active, onExpand, onNavigate }) => {
  const testId = `catalog-menu-level-${level}-${group.node_key.replaceAll("#", "-")}`;
  return <div className={active ? "catalog-menu-row active" : "catalog-menu-row"} onMouseEnter={onExpand}>
    <Link to={group.public_path} onClick={onNavigate} data-testid={`${testId}-link`}>
      <span>{group.name}</span><small>{group.product_count || 0}</small>
    </Link>
    {level < 3 && group.child_count > 0 && <button type="button" onClick={onExpand} aria-label={`Open ${group.name}`} data-testid={`${testId}-expand`}><ChevronRight/></button>}
  </div>;
};

export const CatalogMegaMenu = ({ locale = "en", onNavigate }) => {
  const copy = copyByLocale[locale] || copyByLocale.en;
  const cache = useRef(new Map());
  const closeTimer = useRef(null);
  const root = useRef(null);
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [level2, setLevel2] = useState([]);
  const [level3, setLevel3] = useState([]);
  const [selected1, setSelected1] = useState(null);
  const [selected2, setSelected2] = useState(null);
  const [loading, setLoading] = useState("");
  const [error, setError] = useState(false);

  const loadGroups = useCallback(async (parent = null) => {
    const key = parent || "root";
    if (cache.current.has(key)) return cache.current.get(key);
    const params = parent ? { parent_node_key: parent } : {};
    const { data } = await api.get("/catalog/article-groups", { params });
    cache.current.set(key, data);
    return data;
  }, []);

  const cancelClose = useCallback(() => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }, []);
  const closeMenu = useCallback(() => {
    cancelClose();
    setOpen(false);
  }, [cancelClose]);
  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 450);
  }, [cancelClose]);
  useEffect(() => {
    const outside = event => { if (root.current && !root.current.contains(event.target)) closeMenu(); };
    const escape = event => { if (event.key === "Escape") closeMenu(); };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => { cancelClose(); document.removeEventListener("pointerdown", outside); document.removeEventListener("keydown", escape); };
  }, [cancelClose, closeMenu]);

  const openMenu = async () => {
    cancelClose();
    setOpen(true);
    if (groups.length || loading === "root") return;
    setLoading("root"); setError(false);
    try { setGroups(await loadGroups()); } catch { setError(true); } finally { setLoading(""); }
  };
  const chooseLevel1 = async (group) => {
    if (selected1?.node_key === group.node_key) return;
    setSelected1(group); setSelected2(null); setLevel3([]); setLoading("level2");
    try { setLevel2(await loadGroups(group.node_key)); } catch { setError(true); setLevel2([]); } finally { setLoading(""); }
  };
  const chooseLevel2 = async (group) => {
    if (selected2?.node_key === group.node_key) return;
    setSelected2(group); setLoading("level3");
    try { setLevel3(await loadGroups(group.node_key)); } catch { setError(true); setLevel3([]); } finally { setLoading(""); }
  };
  const closeAndNavigate = () => { closeMenu(); onNavigate?.(); };

  return <div ref={root} className={open ? "catalog-nav open" : "catalog-nav"} onMouseEnter={cancelClose} onMouseLeave={scheduleClose} data-testid="catalog-navigation">
    <div className="catalog-nav-trigger">
      <button type="button" onMouseEnter={openMenu} onClick={() => open ? closeMenu() : openMenu()} aria-expanded={open} aria-controls="aramfix-catalog-mega-menu" aria-label={`${copy.all} menu`} data-testid="catalog-menu-toggle"><FolderTree/>{copy.all}<ChevronDown/></button>
    </div>
    {open && <section id="aramfix-catalog-mega-menu" className="catalog-mega-panel" onMouseEnter={cancelClose} onMouseLeave={scheduleClose} aria-label={copy.products} data-testid="catalog-mega-menu">
      <header><div><FolderTree/><span>{copy.products}</span></div><Link to="/products" onClick={closeAndNavigate} data-testid="catalog-menu-all-products-link">{copy.all}<ChevronRight/></Link></header>
      {error ? <p className="catalog-menu-message error" role="alert" data-testid="catalog-menu-error">{copy.error}</p> : <div className="catalog-menu-grid" aria-live="polite">
        <div className="catalog-menu-column"><h3>{copy.level1}</h3><div className="catalog-menu-list" data-testid="catalog-menu-level-1-list">{loading === "root" ? <span className="catalog-menu-message" data-testid="catalog-menu-level-1-loading">…</span> : groups.map(group => <MenuRow key={group.node_key} group={group} level={1} active={selected1?.node_key === group.node_key} onExpand={() => chooseLevel1(group)} onNavigate={closeAndNavigate}/>)}</div></div>
        <div className="catalog-menu-column"><h3>{copy.level2}</h3><div className="catalog-menu-list" data-testid="catalog-menu-level-2-list">{loading === "level2" ? <span className="catalog-menu-message" data-testid="catalog-menu-level-2-loading">…</span> : level2.length ? level2.map(group => <MenuRow key={group.node_key} group={group} level={2} active={selected2?.node_key === group.node_key} onExpand={() => chooseLevel2(group)} onNavigate={closeAndNavigate}/>) : <span className="catalog-menu-message" data-testid="catalog-menu-level-2-empty">{copy.choose}</span>}</div></div>
        <div className="catalog-menu-column"><h3>{copy.level3}</h3><div className="catalog-menu-list" data-testid="catalog-menu-level-3-list">{loading === "level3" ? <span className="catalog-menu-message" data-testid="catalog-menu-level-3-loading">…</span> : level3.length ? level3.map(group => <MenuRow key={group.node_key} group={group} level={3} onNavigate={closeAndNavigate}/>) : <span className="catalog-menu-message" data-testid="catalog-menu-level-3-empty">{copy.choose}</span>}</div></div>
      </div>}
    </section>}
  </div>;
};